import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SolidorLogo } from "./SolidorLogo";
import { useLang } from "@/i18n";

const safeUpper = (text, lang) =>
  String(text || "").toLocaleUpperCase(
    lang === "tr" ? "tr-TR" : "en-US"
  );

const linkClass =
  "group relative font-mono text-[11px] tracking-[0.28em] transition-opacity duration-300 hover:opacity-60";

const LangSwitch = ({ testidPrefix }) => {
  const { lang, setLang } = useLang();

  const btn = (l) =>
    `transition-opacity duration-300 ${
      lang === l
        ? "opacity-100"
        : "opacity-40 hover:opacity-80"
    }`;

  return (
    <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.28em]">
      <button
        data-testid={`${testidPrefix}-tr`}
        onClick={() => setLang("tr")}
        className={btn("tr")}
        aria-label="Türkçe"
      >
        TR
      </button>

      <span className="opacity-30" aria-hidden>
        /
      </span>

      <button
        data-testid={`${testidPrefix}-en`}
        onClick={() => setLang("en")}
        className={btn("en")}
        aria-label="English"
      >
        EN
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, t } = useLang();

  const LINKS = [
    {
      to: "/#projects",
      label: t.nav.projects,
      testid: "nav-link-projects",
      hash: true,
    },
    {
      to: "/studio",
      label: t.nav.studio,
      testid: "nav-link-studio",
    },
    {
      to: "/services",
      label: t.nav.services,
      testid: "nav-link-services",
    },
    {
      to: "/contact",
      label: t.nav.contact,
      testid: "nav-link-contact",
    },
  ];

  const goProjects = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();

      document
        .getElementById("projects")
        ?.scrollIntoView({
          behavior: "smooth",
        });
    }

    setOpen(false);
  };

  return (
    <>
      <header
        data-testid="site-navigation"
        className="fixed inset-x-0 top-0 z-50 mix-blend-difference text-white"
      >
        <div className="flex items-center justify-between px-5 sm:px-10 py-3">
          <Link
            to="/"
            data-testid="nav-solidor-logo"
            className="flex items-center"
            onClick={() => setOpen(false)}
            aria-label="SOLIDOR"
          >
            <SolidorLogo className="h-11" />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            <nav className="flex items-center gap-10">
              {LINKS.map((l) =>
                l.hash ? (
                  <a
                    key={l.testid}
                    href={l.to}
                    data-testid={l.testid}
                    className={linkClass}
                    onClick={goProjects}
                  >
                    {safeUpper(l.label, lang)}

                    <span className="absolute -bottom-1 left-0 h-px w-0 bg-current transition-[width] duration-300 group-hover:w-full" />
                  </a>
                ) : (
                  <NavLink
                    key={l.testid}
                    to={l.to}
                    data-testid={l.testid}
                    className={linkClass}
                  >
                    {safeUpper(l.label, lang)}

                    <span className="absolute -bottom-1 left-0 h-px w-0 bg-current transition-[width] duration-300 group-hover:w-full" />
                  </NavLink>
                )
              )}
            </nav>

            <LangSwitch testidPrefix="lang-switch" />
          </div>

          <button
            data-testid="nav-mobile-hamburger"
            className="md:hidden font-mono text-[11px] tracking-[0.28em]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {safeUpper(
              open ? t.nav.close : t.nav.menu,
              lang
            )}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-fullscreen-menu"
            className="fixed inset-0 z-40 bg-solidor text-white flex flex-col justify-between px-5 pt-28 pb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <div
                  key={l.testid}
                  className="overflow-hidden"
                >
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{
                      delay: 0.08 * i,
                      duration: 0.6,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {l.hash ? (
                      <a
                        href={l.to}
                        data-testid={`mobile-${l.testid}`}
                        className="block text-3xl font-light tracking-tight py-2"
                        onClick={goProjects}
                      >
                        {safeUpper(l.label, lang)}
                      </a>
                    ) : (
                      <NavLink
                        to={l.to}
                        data-testid={`mobile-${l.testid}`}
                        className="block text-3xl font-light tracking-tight py-2"
                        onClick={() => setOpen(false)}
                      >
                        {safeUpper(l.label, lang)}
                      </NavLink>
                    )}
                  </motion.div>
                </div>
              ))}
            </nav>

            <div className="flex items-end justify-between font-mono text-[10px] tracking-[0.3em] text-white/60">
              <span>
                {safeUpper(
                  t.footer.location,
                  lang
                )}
              </span>

              <LangSwitch testidPrefix="mobile-lang-switch" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
